import { Navigation } from '@aether/ui/Navigation';
import { Link, Outlet, useLocation } from 'react-router';

import { PageHeader, PageTitle } from '@/components/PageHeader';
import { graphql, useMutation, useQuery } from '@/graphql';
import { getAuthApolloClient } from '@/graphql/client';
import { currentOrganizationQuery, useAuth } from '@/utils/auth';

import { OrganizationProvider } from './OrganizationContext';

export function OrganizationLayout() {
  const client = getAuthApolloClient();
  const { currentOrganization: org } = useAuth();
  const { pathname } = useLocation();
  const [inviteMember] = useMutation(inviteMemberMutation, {
    client,
    refetchQueries: [currentOrganizationQuery],
    awaitRefetchQueries: true,
  });
  const [removeMember] = useMutation(removeMemberMutation, {
    client,
    refetchQueries: [currentOrganizationQuery],
    awaitRefetchQueries: true,
  });
  const [updateMember] = useMutation(updateRoleMutation, {
    client,
    refetchQueries: [currentOrganizationQuery],
    awaitRefetchQueries: true,
  });
  const [createTeam] = useMutation(createTeamMutation, {
    client,
    refetchQueries: [currentOrganizationQuery],
    awaitRefetchQueries: true,
  });
  const [deleteTeam] = useMutation(deleteTeamMutation, {
    client,
    refetchQueries: [currentOrganizationQuery],
    awaitRefetchQueries: true,
  });
  const [updateTeam] = useMutation(updateTeamMutation, {
    client,
    refetchQueries: [currentOrganizationQuery],
    awaitRefetchQueries: true,
  });
  const [addMemberToTeam] = useMutation(addMemberToTeamMutation, {
    client,
    refetchQueries: [currentOrganizationQuery],
    awaitRefetchQueries: true,
  });
  const [removeMemberFromTeam] = useMutation(removeMemberFromTeamMutation, {
    client,
    refetchQueries: [currentOrganizationQuery],
    awaitRefetchQueries: true,
  });
  const [createApp] = useMutation(createAppMutation, {
    client,
    refetchQueries: [currentOrganizationQuery],
    awaitRefetchQueries: true,
  });
  const [deleteApp] = useMutation(deleteAppMutation, {
    client,
    refetchQueries: [currentOrganizationQuery],
    awaitRefetchQueries: true,
  });
  const [updateApp] = useMutation(updateAppMutation, {
    client,
    refetchQueries: [currentOrganizationQuery],
    awaitRefetchQueries: true,
  });
  const [deleteApiKey] = useMutation(deleteApiKeyMutation, {
    client,
    refetchQueries: [currentOrganizationQuery],
    awaitRefetchQueries: true,
  });
  const [updateApiKey] = useMutation(updateApiKeyMutation, {
    client,
    refetchQueries: [currentOrganizationQuery],
    awaitRefetchQueries: true,
  });
  const [createApiKey] = useMutation(createApiKeyMutation, {
    client,
    refetchQueries: [currentOrganizationQuery],
    awaitRefetchQueries: true,
  });
  const { data: pendingMembers } = useQuery(pendingMembersQuery, {
    client,
    variables: { organizationId: org?.id || '' },
    skip: !org || (org.role !== 'owner' && org.role !== 'admin'),
  });
  if (!org) return <div>No organization found</div>;
  return (
    <>
      <PageHeader>
        <PageTitle className="my-4">{org.name}</PageTitle>
        <Navigation.Menu className="-mb-[1px] gap-0">
          <Navigation.Item isActive={pathname.includes('/organization/settings')} asChild variant="tab">
            <Link to="/organization/settings">Settings</Link>
          </Navigation.Item>
          <Navigation.Item
            isActive={pathname.includes('/organization/members')}
            asChild
            variant="tab"
            className="gap-1"
          >
            <Link to="/organization/members">
              Members ({org.members.filter(m => m.type === 'user').length})
              {pendingMembers && pendingMembers.organizationPendingMembers.length > 0 && (
                <span className="-mx-2 ml-2 h-4 rounded-full bg-orange-500 px-1 text-center text-xxs font-semibold leading-4 text-white">
                  {pendingMembers.organizationPendingMembers.length} pending
                </span>
              )}
            </Link>
          </Navigation.Item>
          <Navigation.Item isActive={pathname.includes('/organization/teams')} asChild variant="tab">
            <Link to="/organization/teams">Teams ({org.teams.length})</Link>
          </Navigation.Item>
          <Navigation.Item isActive={pathname.includes('/organization/service-accounts')} asChild variant="tab">
            <Link to="/organization/service-accounts">
              Service-accounts ({org.members.filter(m => m.type === 'app').length})
            </Link>
          </Navigation.Item>
        </Navigation.Menu>
      </PageHeader>
      <main className="centered-page mb-6 mt-8">
        <OrganizationProvider
          data={{
            organization: org,
            currentUserRole: org.role,
            removeMember: (clientId: string) =>
              removeMember({ variables: { input: { clientId, organizationId: org.id } } }),
            addMember: () => Promise.reject(),
            inviteMember: data =>
              inviteMember({
                variables: {
                  input: { email: data.email, role: data.role, name: data.displayName, organizationId: org.id },
                },
              }),
            updateMemberRole: data =>
              updateMember({
                variables: { input: { clientId: data.clientId, organizationId: org.id, role: data.role } },
              }),
            createTeam: data =>
              createTeam({
                variables: { input: { name: data.name, description: data.description, organizationId: org.id } },
              }),
            updateTeam: data =>
              updateTeam({
                variables: {
                  input: { organizationId: org.id, teamId: data.id, name: data.name, description: data.description },
                },
              }),
            removeTeam: id => deleteTeam({ variables: { input: { teamId: id, organizationId: org.id } } }),
            createServiceAccount: data =>
              createApp({
                variables: { input: { name: data.name, role: data.role, organizationId: org.id } },
              }),
            updateServiceAccount: data =>
              updateApp({
                variables: {
                  input: { name: data.name, appId: data.id },
                },
              }),
            removeServiceAccount: id => deleteApp({ variables: { input: { appId: id } } }),
            addMemberToTeam: data =>
              addMemberToTeam({
                variables: { input: { organizationId: org.id, teamId: data.teamId, clientId: data.clientId } },
              }),
            removeMemberFromTeam: data =>
              removeMemberFromTeam({
                variables: { input: { organizationId: org.id, teamId: data.teamId, clientId: data.clientId } },
              }),
            createApiKey: data => createApiKey({ variables: { input: { ...data } } }),
            updateApiKey: data => updateApiKey({ variables: { input: { ...data } } }),
            removeApiKey: data => deleteApiKey({ variables: { input: { ...data } } }),
          }}
        >
          <Outlet />
        </OrganizationProvider>
      </main>
    </>
  );
}

const inviteMemberMutation = graphql(/* GraphQL */ `
  mutation InviteMember($input: InviteMemberInput!) {
    inviteMember(input: $input) {
      ...OrganizationMember
    }
  }
`);

const removeMemberMutation = graphql(/* GraphQL */ `
  mutation RemoveMember($input: RemoveMemberInput!) {
    removeMember(input: $input)
  }
`);

const updateRoleMutation = graphql(/* GraphQL */ `
  mutation UpdateRole($input: UpdateRoleInput!) {
    updateRole(input: $input) {
      ...OrganizationMember
    }
  }
`);

const createTeamMutation = graphql(/* GraphQL */ `
  mutation CreateTeam($input: CreateTeamInput!) {
    createTeam(input: $input) {
      ...OrganizationTeam
    }
  }
`);

const deleteTeamMutation = graphql(/* GraphQL */ `
  mutation DeleteTeam($input: DeleteTeamInput!) {
    deleteTeam(input: $input)
  }
`);

const updateTeamMutation = graphql(/* GraphQL */ `
  mutation UpdateTeam($input: UpdateTeamInput!) {
    updateTeam(input: $input) {
      ...OrganizationTeam
    }
  }
`);

const addMemberToTeamMutation = graphql(/* GraphQL */ `
  mutation AddMemberToTeam($input: AddMemberToTeamInput!) {
    addMemberToTeam(input: $input) {
      ...OrganizationTeam
    }
  }
`);

const removeMemberFromTeamMutation = graphql(/* GraphQL */ `
  mutation RemoveMemberFromTeam($input: RemoveMemberFromTeamInput!) {
    removeMemberFromTeam(input: $input) {
      ...OrganizationTeam
    }
  }
`);

export const pendingMembersQuery = graphql(/* GraphQL */ `
  query OrganizationPendingMembers($organizationId: ID!) {
    organizationPendingMembers(organizationId: $organizationId) {
      ...OrganizationMemberPending
    }
  }
`);

const createAppMutation = graphql(/* GraphQL */ `
  mutation CreateApp($input: CreateAppInput!) {
    createApp(input: $input) {
      ...AppFragment
    }
  }
`);

const deleteAppMutation = graphql(/* GraphQL */ `
  mutation DeleteApp($input: DeleteAppInput!) {
    deleteApp(input: $input)
  }
`);

const updateAppMutation = graphql(/* GraphQL */ `
  mutation UpdateApp($input: UpdateAppInput!) {
    updateApp(input: $input) {
      ...AppFragment
    }
  }
`);

const deleteApiKeyMutation = graphql(/* GraphQL */ `
  mutation DeleteApiKey($input: DeleteApiKeyInput!) {
    deleteApiKey(input: $input)
  }
`);

const updateApiKeyMutation = graphql(/* GraphQL */ `
  mutation UpdateApiKey($input: UpdateApIKeyInput!) {
    updateApiKey(input: $input) {
      id
      active
    }
  }
`);

const createApiKeyMutation = graphql(/* GraphQL */ `
  mutation CreateApiKey($input: CreateApiKeyInput!) {
    createApiKey(input: $input)
  }
`);
